import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Power, Loader2, AlertTriangle, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api, { updateTrainerLiveLocation } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const REFRESH_MS = 60_000;

/**
 * Trainer-only "Go Online / Go Offline" toggle. When online, requests GPS
 * and refreshes location every 60 seconds. When offline, scrubs location.
 */
const GoOnlineToggle = () => {
  const { user, refreshUser } = useAuth();
  const initialOnline = !!user?.trainer_profile?.is_online_now;
  const [online, setOnline] = useState(initialOnline);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const intervalRef = useRef(null);
  const watchIdRef = useRef(null);

  const clear = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (watchIdRef.current && navigator.geolocation) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (e) { /* ignore */ }
      watchIdRef.current = null;
    }
  }, []);

  useEffect(() => () => clear(), [clear]);

  // If user toggled online elsewhere and reloaded, start the watcher
  useEffect(() => {
    if (online && !intervalRef.current) {
      startLocationLoop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startLocationLoop = () => {
    if (!('geolocation' in navigator)) return;
    const pushOnce = () => {
      navigator.geolocation.getCurrentPosition(
        (p) => { updateTrainerLiveLocation(p.coords.latitude, p.coords.longitude).catch(() => {}); },
        () => {},
        { enableHighAccuracy: true, maximumAge: 30_000, timeout: 15_000 }
      );
    };
    pushOnce();
    intervalRef.current = setInterval(pushOnce, REFRESH_MS);
  };

  const goOnline = async () => {
    setBusy(true);
    setDenied(false);
    if (!('geolocation' in navigator)) {
      setDenied(true);
      toast.error('Geolocation isn\u2019t available in this browser.');
      setBusy(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          await api.post('/availability/online', { is_online_now: true, lat, lng });
          setOnline(true);
          startLocationLoop();
          await refreshUser?.();
          toast.success('You\u2019re online. Clients can find you on the map.');
        } catch (e) {
          toast.error('Could not go online');
        } finally {
          setBusy(false);
        }
      },
      async () => {
        setDenied(true);
        setBusy(false);
        toast.error('Enable location to go online and appear on the map.');
      },
      { enableHighAccuracy: true, timeout: 15_000 }
    );
  };

  const goOffline = async () => {
    setBusy(true);
    try {
      clear();
      await api.post('/availability/online', { is_online_now: false });
      setOnline(false);
      await refreshUser?.();
      toast.message('You\u2019re offline. Removed from the map.');
    } catch (e) {
      toast.error('Could not go offline');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-5" data-testid="go-online-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-wider text-zinc-500 font-semibold mb-1">Availability</p>
          <p className={`text-lg font-bold ${online ? 'text-emerald-400' : 'text-zinc-300'}`}>
            {online ? 'You\u2019re online' : 'You\u2019re offline'}
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            {online
              ? 'Live location refreshes every 60 seconds.'
              : 'Toggle online to share your live location and appear on the Find a Trainer map.'}
          </p>
        </div>
        <button
          type="button"
          onClick={online ? goOffline : goOnline}
          disabled={busy}
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors shrink-0 ${
            online ? 'bg-emerald-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
          }`}
          aria-label={online ? 'Go offline' : 'Go online'}
          data-testid="go-online-btn"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Power className="w-5 h-5" />}
        </button>
      </div>

      {denied && !online && (
        <div className="mt-4 p-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200 flex items-start gap-2" data-testid="geo-denied">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Enable location to go online and appear on the map.</span>
        </div>
      )}
      {online && (
        <div className="mt-4 flex items-center gap-2 text-xs text-emerald-300" data-testid="go-online-live">
          <MapPin className="w-3 h-3" /> Broadcasting live location
        </div>
      )}
    </div>
  );
};

export default GoOnlineToggle;
